import React, { useEffect, useState } from 'react';
import { X, Clock, FileText, Loader2 } from 'lucide-react';
import { Revision } from '../../types';
import { getRevisions } from '../../services/revisions';

interface RevisionListProps {
  projectId: string;
  onClose: () => void;
  onPreview: (revision: Revision) => void;
}

function formatDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return `Today at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  } else if (diffDays === 1) {
    return `Yesterday at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  } else if (diffDays < 7) {
    return `${date.toLocaleDateString([], { weekday: 'long' })} at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function RevisionList({ projectId, onClose, onPreview }: RevisionListProps) {
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getRevisions(projectId)
      .then((data) => {
        setRevisions(data);
        setError(null);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load revisions');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectId]);

  return (
    <div className="h-full flex flex-col bg-editor-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-editor-border">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-blue-600" />
          <h2 className="font-semibold text-gray-900">Revision History</h2>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"
          title="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-600">{error}</div>
        ) : revisions.length === 0 ? (
          <div className="p-8 text-center">
            <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No revisions yet.</p>
            <p className="text-gray-400 text-sm mt-1">
              Compile your document to create the first checkpoint.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-editor-border">
            {revisions.map((revision) => (
              <li
                key={revision.id}
                className="px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {revision.label || formatDate(revision.createdAt)}
                    </p>
                    {revision.label && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatDate(revision.createdAt)}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {revision.fileCount} {revision.fileCount === 1 ? 'file' : 'files'}
                      </span>
                      <span>{formatSize(revision.pdfSize)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => onPreview(revision)}
                    className="ml-4 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    Preview
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
